import {Fragment, useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
    Accordion,
    AccordionDetails,
    AccordionTitle,
    ConfirmModal
} from 'components';
import {TRANSLATION_KEYS} from 'locale';
import {ClientSettings, DynamicContentComponent} from 'types';
import {buildTranslationKey} from 'utils/transform';

import {ComponentDescription} from '.././ComponentDescription';
import {WhiteLabelEditor} from '../WhiteLabelEditor';

import {WhiteLabelPreview} from './WhiteLabelPreview';

const T_KEYS = TRANSLATION_KEYS.USER.CONTENT.CLIENT_SESSION.WHITE_LABEL;

export type WhiteLabelPreviewModalProps = {
    open: boolean;
    component: DynamicContentComponent;
    clientSettings: ClientSettings;
    onTemplateUpdate?: (name: string, value: string) => void;
    onStylesUpdate?: (name: string, value: string) => void;
    onResetTemplate?: (name: string) => void;
    onResetStyles?: (name: string) => void;
    onClose: () => void;
};

export const WhiteLabelPreviewModal: React.FC<WhiteLabelPreviewModalProps> = ({
    open, component, clientSettings, onTemplateUpdate, onStylesUpdate, onResetTemplate, onResetStyles, onClose
}) => {
    const {t} = useTranslation();
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
    const toggleIsFullScreen = useCallback(() => {
        setIsFullScreen((prev) => !prev);
    }, [setIsFullScreen]);

    const title = useMemo(() => {
        const translationKey = buildTranslationKey(component?.name);
        return `${t(T_KEYS.PREVIEW_MODAL.TITLE)}:  ${t(T_KEYS.COMPONENTS?.[translationKey]?.NAME)}`;
    }, [t, component?.name]);

    return (
        <ConfirmModal
            title={title}
            open={open}
            maxWidth='xl'
            minWidth='1/2'
            hideCancelButton
            fullScreen={isFullScreen}
            onClose={onClose}
            onFullScreenChange={toggleIsFullScreen}
        >
            {open && component &&
                <Fragment>
                    <WhiteLabelPreview
                        componentName={component.name}
                        clientSettings={clientSettings}
                        template={component.template}
                        styles={component.styles}
                    />
                    {isFullScreen &&
                        <Accordion className='tw-bg-light tw-p-2 tw-mt-6'>
                            <AccordionTitle>{t(T_KEYS.PREVIEW_MODAL.LIVE_EDIT)}</AccordionTitle>
                            <AccordionDetails>
                                <ComponentDescription name={component.name} />
                                <div className='md:tw-grid md:tw-grid-cols-2 md:tw-gap-8 tw-mb-2'>
                                    <WhiteLabelEditor
                                        language='html'
                                        placeholder={t(T_KEYS.TEMPLATE_PLACEHOLDER)}
                                        value={component.template}
                                        onUpdate={(value) => onTemplateUpdate(component.name, value)}
                                        onReset={() => onResetTemplate(component.name)}
                                    />
                                    <WhiteLabelEditor
                                        language='css'
                                        placeholder={t(T_KEYS.STYLES_PLACEHOLDER)}
                                        value={component.styles}
                                        onUpdate={(value) => onStylesUpdate(component.name, value)}
                                        onReset={() => onResetStyles(component.name)}
                                    />
                                </div>
                            </AccordionDetails>
                        </Accordion>}
                </Fragment>}
        </ConfirmModal>);
};